/**
 * YearComparisonView - Displays side-by-side comparison of current and previous year data
 * Updated to show financial items separately from operating items
 */

import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import type { SIEProcessedData, ComparisonRow, IncomeStatementItem } from './types';
import { formatAmountWithDisplayFormat, determineOptimalUnit, formatPercent, calculatePercentChange, getChangeFlag, type DisplayUnit, type DisplayFormat } from './utils';

interface YearComparisonViewProps {
  currentYear: SIEProcessedData;
  previousYear: SIEProcessedData;
  displayFormat?: DisplayFormat;
}

export function YearComparisonView({ currentYear, previousYear, displayFormat = 'auto' }: YearComparisonViewProps) {
  // Collect all amounts to determine optimal display unit (used when displayFormat is 'auto')
  const autoUnit: DisplayUnit = useMemo(() => {
    const currIncome = currentYear.aggregated.incomeStatement;
    const prevIncome = previousYear.aggregated.incomeStatement;
    
    const allAmounts: number[] = [
      currIncome.totalRevenue,
      prevIncome.totalRevenue,
      currIncome.totalExpenses,
      prevIncome.totalExpenses,
      currIncome.operatingResult ?? (currIncome.totalRevenue - currIncome.totalExpenses),
      prevIncome.operatingResult ?? (prevIncome.totalRevenue - prevIncome.totalExpenses),
      currIncome.financialNet ?? 0,
      prevIncome.financialNet ?? 0,
      currIncome.netResult,
      prevIncome.netResult,
      // Add individual account amounts
      ...(currIncome.revenue || []).map(r => Math.abs(r.amount)),
      ...(prevIncome.revenue || []).map(r => Math.abs(r.amount)),
      ...(currIncome.expenses || []).map(r => Math.abs(r.amount)),
      ...(prevIncome.expenses || []).map(r => Math.abs(r.amount)),
    ];
    
    return determineOptimalUnit(allAmounts);
  }, [currentYear, previousYear]);

  // Helper to format amounts with selected format
  const fmt = (amount: number) => formatAmountWithDisplayFormat(amount, displayFormat, autoUnit);

  // Helper function to process account items into comparison rows
  const processAccountItems = (
    currentItems: IncomeStatementItem[],
    previousItems: IncomeStatementItem[]
  ): ComparisonRow[] => {
    const rows: ComparisonRow[] = [];
    const prevMap = new Map(previousItems.map(item => [item.account, item]));
    const currMap = new Map(currentItems.map(item => [item.account, item]));

    // Process current items
    for (const curr of currentItems) {
      const prev = prevMap.get(curr.account);
      const currAmount = Math.abs(curr.amount);
      const prevAmount = prev ? Math.abs(prev.amount) : 0;
      const absoluteChange = currAmount - prevAmount;
      const percentChange = calculatePercentChange(currAmount, prevAmount);
      
      rows.push({
        account: curr.account,
        name: curr.name,
        currentYear: currAmount,
        previousYear: prevAmount,
        absoluteChange,
        percentChange,
        flag: !prev ? 'new' : getChangeFlag(percentChange),
      });
    }

    // Check for removed items
    for (const prev of previousItems) {
      if (!currMap.has(prev.account)) {
        rows.push({
          account: prev.account,
          name: prev.name,
          currentYear: 0,
          previousYear: Math.abs(prev.amount),
          absoluteChange: -Math.abs(prev.amount),
          percentChange: -100,
          flag: 'removed',
        });
      }
    }

    return rows.sort((a, b) => a.account.localeCompare(b.account));
  };

  // Process all categories
  const { revenueRows, expenseRows, financialIncomeRows, financialExpenseRows, appropriationRows, taxRows } = useMemo(() => {
    const currIncome = currentYear.aggregated.incomeStatement;
    const prevIncome = previousYear.aggregated.incomeStatement;

    return {
      revenueRows: processAccountItems(currIncome.revenue || [], prevIncome.revenue || []),
      expenseRows: processAccountItems(currIncome.expenses || [], prevIncome.expenses || []),
      financialIncomeRows: processAccountItems(currIncome.financialIncome || [], prevIncome.financialIncome || []),
      financialExpenseRows: processAccountItems(currIncome.financialExpenses || [], prevIncome.financialExpenses || []),
      appropriationRows: processAccountItems(currIncome.appropriations || [], prevIncome.appropriations || []),
      taxRows: processAccountItems(currIncome.taxes || [], prevIncome.taxes || []),
    };
  }, [currentYear, previousYear]);

  // Summary calculations
  const summary = useMemo(() => {
    const currIncome = currentYear.aggregated.incomeStatement;
    const prevIncome = previousYear.aggregated.incomeStatement;

    return {
      revenue: {
        current: currIncome.totalRevenue,
        previous: prevIncome.totalRevenue,
        change: calculatePercentChange(currIncome.totalRevenue, prevIncome.totalRevenue),
      },
      expenses: {
        current: currIncome.totalExpenses,
        previous: prevIncome.totalExpenses,
        change: calculatePercentChange(currIncome.totalExpenses, prevIncome.totalExpenses),
      },
      operatingResult: {
        current: currIncome.operatingResult ?? (currIncome.totalRevenue - currIncome.totalExpenses),
        previous: prevIncome.operatingResult ?? (prevIncome.totalRevenue - prevIncome.totalExpenses),
        change: calculatePercentChange(
          currIncome.operatingResult ?? (currIncome.totalRevenue - currIncome.totalExpenses),
          prevIncome.operatingResult ?? (prevIncome.totalRevenue - prevIncome.totalExpenses)
        ),
      },
      financialNet: {
        current: currIncome.financialNet ?? 0,
        previous: prevIncome.financialNet ?? 0,
        change: calculatePercentChange(currIncome.financialNet ?? 0, prevIncome.financialNet ?? 0),
      },
      result: {
        current: currIncome.netResult,
        previous: prevIncome.netResult,
        change: calculatePercentChange(currIncome.netResult, prevIncome.netResult),
      },
    };
  }, [currentYear, previousYear]);

  const getFlagIcon = (flag: ComparisonRow['flag']) => {
    switch (flag) {
      case 'critical':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-warning" />;
      case 'new':
        return <Plus className="h-4 w-4 text-success" />;
      case 'removed':
        return <Trash2 className="h-4 w-4 text-muted-foreground" />;
      default:
        return null;
    }
  };

  const getFlagBadge = (flag: ComparisonRow['flag']) => {
    switch (flag) {
      case 'critical':
        return <Badge variant="destructive" className="text-xs">Kritisk</Badge>;
      case 'warning':
        return <Badge className="bg-warning/20 text-warning-foreground border-warning/30 text-xs">Varning</Badge>;
      case 'new':
        return <Badge className="bg-success/20 text-success border-success/30 text-xs">Ny</Badge>;
      case 'removed':
        return <Badge variant="secondary" className="text-xs">Borttagen</Badge>;
      default:
        return null;
    }
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) return <TrendingUp className="h-4 w-4 text-success" />;
    if (change < 0) return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const currentFiscalYear = currentYear.metadata.fiscalYears.find(fy => fy.index === 0);
  const previousFiscalYear = previousYear.metadata.fiscalYears.find(fy => fy.index === 0);

  const currentYearLabel = currentFiscalYear?.end?.slice(0, 4) || 'Innevarande';
  const previousYearLabel = previousFiscalYear?.end?.slice(0, 4) || 'Föregående';

  // Render a section of account rows
  const renderAccountRows = (rows: ComparisonRow[], isExpense: boolean = false) => (
    rows.map(row => (
      <TableRow key={row.account} className={row.flag === 'removed' ? 'opacity-50' : ''}>
        <TableCell>
          <div className="flex items-center gap-2">
            {getFlagIcon(row.flag)}
            <span className="font-mono text-xs">{row.account}</span>
            <span className="text-muted-foreground">{row.name}</span>
          </div>
        </TableCell>
        <TableCell className="text-right tabular-nums whitespace-nowrap">
          {fmt(row.currentYear)}
        </TableCell>
        <TableCell className="text-right tabular-nums whitespace-nowrap">
          {fmt(row.previousYear)}
        </TableCell>
        <TableCell className={`text-right tabular-nums whitespace-nowrap ${
          isExpense 
            ? (row.absoluteChange <= 0 ? 'text-success' : 'text-destructive')
            : (row.absoluteChange >= 0 ? 'text-success' : 'text-destructive')
        }`}>
          {fmt(row.absoluteChange)}
        </TableCell>
        <TableCell className={`text-right tabular-nums ${
          isExpense 
            ? (row.percentChange <= 0 ? 'text-success' : 'text-destructive')
            : (row.percentChange >= 0 ? 'text-success' : 'text-destructive')
        }`}>
          {formatPercent(row.percentChange)}
        </TableCell>
        <TableCell>
          {getFlagBadge(row.flag)}
        </TableCell>
      </TableRow>
    ))
  );

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-5">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {getChangeIcon(summary.revenue.change)}
              Intäkter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{currentYearLabel}</span>
                <span className="font-medium whitespace-nowrap">{fmt(summary.revenue.current)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{previousYearLabel}</span>
                <span className="font-medium whitespace-nowrap">{fmt(summary.revenue.previous)}</span>
              </div>
              <div className={`text-lg font-bold ${summary.revenue.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                {formatPercent(summary.revenue.change)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {getChangeIcon(-summary.expenses.change)}
              Kostnader
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{currentYearLabel}</span>
                <span className="font-medium whitespace-nowrap">{fmt(summary.expenses.current)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{previousYearLabel}</span>
                <span className="font-medium whitespace-nowrap">{fmt(summary.expenses.previous)}</span>
              </div>
              <div className={`text-lg font-bold ${summary.expenses.change <= 0 ? 'text-success' : 'text-destructive'}`}>
                {formatPercent(summary.expenses.change)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {getChangeIcon(summary.operatingResult.change)}
              Rörelseresultat
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{currentYearLabel}</span>
                <span className={`font-medium whitespace-nowrap ${summary.operatingResult.current >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {fmt(summary.operatingResult.current)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{previousYearLabel}</span>
                <span className={`font-medium whitespace-nowrap ${summary.operatingResult.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {fmt(summary.operatingResult.previous)}
                </span>
              </div>
              <div className={`text-lg font-bold ${summary.operatingResult.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                {formatPercent(summary.operatingResult.change)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {getChangeIcon(summary.financialNet.change)}
              Finansnetto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{currentYearLabel}</span>
                <span className={`font-medium whitespace-nowrap ${summary.financialNet.current >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {fmt(summary.financialNet.current)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{previousYearLabel}</span>
                <span className={`font-medium whitespace-nowrap ${summary.financialNet.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {fmt(summary.financialNet.previous)}
                </span>
              </div>
              <div className={`text-lg font-bold ${summary.financialNet.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                {formatPercent(summary.financialNet.change)}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              {getChangeIcon(summary.result.change)}
              Årets resultat
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{currentYearLabel}</span>
                <span className={`font-medium whitespace-nowrap ${summary.result.current >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {fmt(summary.result.current)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{previousYearLabel}</span>
                <span className={`font-medium whitespace-nowrap ${summary.result.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                  {fmt(summary.result.previous)}
                </span>
              </div>
              <div className={`text-lg font-bold ${summary.result.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                {formatPercent(summary.result.change)}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Jämförelse resultaträkning</CardTitle>
          <CardDescription>
            Konton markerade med varning har avvikelser över 10%, kritiska avvikelser över 25%
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="w-full">
            <div className="min-w-[700px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[200px]">Konto</TableHead>
                    <TableHead className="text-right min-w-[120px]">{currentYearLabel}</TableHead>
                    <TableHead className="text-right min-w-[120px]">{previousYearLabel}</TableHead>
                    <TableHead className="text-right min-w-[120px]">Förändring</TableHead>
                    <TableHead className="text-right min-w-[80px]">%</TableHead>
                    <TableHead className="min-w-[80px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Revenue Section */}
                  <TableRow className="bg-success/5">
                    <TableCell colSpan={6} className="font-semibold text-success">
                      Rörelseintäkter (3xxx)
                    </TableCell>
                  </TableRow>
                  {renderAccountRows(revenueRows, false)}

                  {/* Operating Expenses Section */}
                  <TableRow className="bg-destructive/5">
                    <TableCell colSpan={6} className="font-semibold text-destructive">
                      Rörelsekostnader (4xxx-7xxx)
                    </TableCell>
                  </TableRow>
                  {renderAccountRows(expenseRows, true)}

                  {/* Operating Result Subtotal */}
                  <TableRow className="bg-muted/50 font-semibold">
                    <TableCell>Rörelseresultat</TableCell>
                    <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.operatingResult.current >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {fmt(summary.operatingResult.current)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.operatingResult.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {fmt(summary.operatingResult.previous)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.operatingResult.current - summary.operatingResult.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {fmt(summary.operatingResult.current - summary.operatingResult.previous)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${summary.operatingResult.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {formatPercent(summary.operatingResult.change)}
                    </TableCell>
                    <TableCell />
                  </TableRow>

                  {/* Financial Income Section */}
                  {financialIncomeRows.length > 0 && (
                    <>
                      <TableRow className="bg-success/5">
                        <TableCell colSpan={6} className="font-semibold text-success">
                          Finansiella intäkter (80xx-82xx)
                        </TableCell>
                      </TableRow>
                      {renderAccountRows(financialIncomeRows, false)}
                    </>
                  )}

                  {/* Financial Expenses Section */}
                  {financialExpenseRows.length > 0 && (
                    <>
                      <TableRow className="bg-destructive/5">
                        <TableCell colSpan={6} className="font-semibold text-destructive">
                          Finansiella kostnader (83xx)
                        </TableCell>
                      </TableRow>
                      {renderAccountRows(financialExpenseRows, true)}
                    </>
                  )}

                  {/* Financial Net Subtotal */}
                  {(financialIncomeRows.length > 0 || financialExpenseRows.length > 0) && (
                    <TableRow className="bg-muted/50 font-semibold">
                      <TableCell>Finansnetto</TableCell>
                      <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.financialNet.current >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {fmt(summary.financialNet.current)}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.financialNet.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {fmt(summary.financialNet.previous)}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.financialNet.current - summary.financialNet.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {fmt(summary.financialNet.current - summary.financialNet.previous)}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums ${summary.financialNet.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {formatPercent(summary.financialNet.change)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  )}

                  {/* Appropriations Section */}
                  {appropriationRows.length > 0 && (
                    <>
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={6} className="font-semibold">
                          Bokslutsdispositioner (88xx)
                        </TableCell>
                      </TableRow>
                      {renderAccountRows(appropriationRows, false)}
                    </>
                  )}

                  {/* Tax Section */}
                  {taxRows.length > 0 && (
                    <>
                      <TableRow className="bg-destructive/5">
                        <TableCell colSpan={6} className="font-semibold text-destructive">
                          Skatt på årets resultat (86xx-89xx)
                        </TableCell>
                      </TableRow>
                      {renderAccountRows(taxRows, true)}
                    </>
                  )}

                  {/* Net Result */}
                  <TableRow className="bg-primary/10 font-bold">
                    <TableCell>Årets resultat</TableCell>
                    <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.result.current >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {fmt(summary.result.current)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.result.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {fmt(summary.result.previous)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums whitespace-nowrap ${summary.result.current - summary.result.previous >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {fmt(summary.result.current - summary.result.previous)}
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${summary.result.change >= 0 ? 'text-success' : 'text-destructive'}`}>
                      {formatPercent(summary.result.change)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
